import { useCallback, useEffect, useRef, useState } from "react";
import { useAppConfig } from "@/appConfig";
import { getUserType } from "@/helpers/common";
import {
  isMessageComplianceActive,
  COMPLIANCE_DEBOUNCE_MS,
  COMPLIANCE_FULL_TIMEOUT_MS,
} from "@/helpers/messageCompliance";
import { checkMessageCompliance } from "@/services/v2/messageComplianceService";

const INERT = { violations: [], severity: "none" };

// Drives the message-compliance banner for an agent's draft message.
// - Runs the `screen` (regex + LLM, unpersisted, cached) check on a debounce while typing.
// - Exposes `runFullCheck()` for the authoritative `full` (regex + LLM) check,
//   meant to be awaited once on Send.
// Inert (no calls, no banner) unless `isMessageComplianceActive` passes.
// Always fails open: a check error never blocks typing or sending.
// `context` ({ source, threadId }) is sent with the `full` check only, so the
// stored flag says which surface and conversation it came from. `runFullCheck`
// also accepts overrides for values only known at send time.
export default function useComplianceCheck(text, context = {}) {
  const { source, threadId } = context;
  const appConfig = useAppConfig();
  const active = isMessageComplianceActive(appConfig, getUserType());

  const [violations, setViolations] = useState([]);
  const [severity, setSeverity] = useState("none");
  const [loading, setLoading] = useState(false);

  // Latest text, so async responses for stale drafts can be discarded.
  const latestText = useRef(text);
  latestText.current = text;

  // Debounced screen (regex + LLM, unpersisted) check while the agent types.
  useEffect(() => {
    if (!active) return undefined;
    if ((text || "").trim().length === 0) {
      setViolations([]);
      setSeverity("none");
      return undefined;
    }
    // Aborted on the next keystroke/unmount so superseded drafts don't keep
    // requests in flight.
    const controller = new AbortController();
    const handle = setTimeout(async () => {
      try {
        const res = await checkMessageCompliance(
          text,
          "screen",
          controller.signal,
        );
        if (latestText.current !== text) return; // stale draft, ignore
        const result = res?.result || INERT;
        setViolations(result.violations || []);
        setSeverity(result.severity || "none");
      } catch (e) {
        if (controller.signal.aborted) return;
        // Rate-limited: keep whatever warning is already showing.
        if (e?.response?.status === 429) return;
        if (latestText.current === text) {
          setViolations([]);
          setSeverity("none");
        }
      }
    }, COMPLIANCE_DEBOUNCE_MS);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [text, active]);

  // Authoritative full (regex + LLM, paid) check — await once on Send.
  const runFullCheck = useCallback(
    async (overrides = {}) => {
      if (!active || (text || "").trim().length === 0) return INERT;
      setLoading(true);
      // Fail open if the check outlives the timeout (abort rejects below).
      const controller = new AbortController();
      const timer = setTimeout(
        () => controller.abort(),
        COMPLIANCE_FULL_TIMEOUT_MS,
      );
      try {
        const res = await checkMessageCompliance(
          text,
          "full",
          controller.signal,
          { source, threadId, ...overrides },
        );
        const result = res?.result || INERT;
        if (latestText.current === text) {
          setViolations(result.violations || []);
          setSeverity(result.severity || "none");
        }
        return result;
      } catch (e) {
        return INERT; // fail open — let the send proceed
      } finally {
        clearTimeout(timer);
        setLoading(false);
      }
    },
    [text, active, source, threadId],
  );

  if (!active) {
    return { violations: [], severity: "none", loading: false, runFullCheck };
  }
  return { violations, severity, loading, runFullCheck };
}
